import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { WebPageSchema, BreadcrumbListSchema, OrganizationSchema } from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormMessage('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复您。');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qsuesnlsngrm4lm5f0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText || '';

      if (response.ok && ((parsed?.code as string) === 'OK')) {
        setFormStatus('success');
        setFormMessage('消息已发送，我们会尽快回复您。');
        form.reset();
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam') || !response.ok) {
        setFormStatus('error');
        setFormMessage(serverMsg || '发送失败，请稍后重试。');
      } else {
        setFormStatus('error');
        setFormMessage(serverMsg || '发送失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络异常，请检查网络后重试。');
    }
  };

  return (
    <>
      <OrganizationSchema />
      <WebPageSchema
        name="关于我们 - 红桃视频"
        description="了解红桃视频的品牌故事、核心价值观、用户口碑及联系方式。红桃视频是国内领先的正版高清视频娱乐平台。"
        url="https://hongtaovideo.com/about"
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://hongtaovideo.com/' },
          { name: '关于我们', url: 'https://hongtaovideo.com/about' },
        ]}
      />
      <Navbar />
      <main className="pt-20 md:pt-24">
        {/* Hero */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-700 font-medium">关于我们</span>
            </nav>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-4 text-center">
              关于<span className="text-primary-500">红桃视频</span>
            </h1>
            <p className="text-sm md:text-base text-foreground-500 text-center max-w-2xl mx-auto leading-relaxed">
              红桃视频是国内领先的正版高清视频娱乐平台，致力于为全球华语用户提供丰富、
              优质、安全的在线视频观看体验。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                  红桃视频成立于2018年，总部位于中国北京。自成立以来，我们始终秉持
                  <strong>「让每个人都能享受高品质视听娱乐」</strong>的使命，
                  致力于打造国内一流的正版视频内容平台。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                  经过多年的深耕发展，红桃视频已建立起涵盖电视剧、电影、综艺、动漫、
                  纪录片等多元品类的海量内容库。平台累计注册用户已突破5000万，
                  日均活跃用户超过800万，稳居国内视频平台第一梯队。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们不仅注重内容的广度，更注重品质的深度。红桃视频与国内外超过300家
                  影视制作公司和发行机构建立了长期合作关系，确保每一部上线作品都经过严格筛选，
                  为用户提供真正优质的观影选择。
                </p>
              </div>
              <div className="relative h-64 md:h-80 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20office%20space%20with%20a%20large%20screen%20displaying%20video%20content%2C%20team%20collaboration%20area%2C%20warm%20lighting%2C%20contemporary%20furniture%2C%20tech%20company%20vibe%2C%20plants%20and%20natural%20elements%2C%20professional%20yet%20creative%20atmosphere%2C%20clean%20aesthetic&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="红桃视频办公环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">核心价值观</h2>
            <p className="text-sm text-foreground-500 text-center mb-10 md:mb-14">指引我们前行的信念</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '正版承诺', desc: '所有内容均获得正规版权授权，坚决抵制盗版，支持优质内容创作' },
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户体验为中心，持续优化产品功能和服务质量' },
                { icon: 'ri-rocket-line', title: '技术驱动', desc: '投入前沿技术研发，AI推荐、4K HDR、杜比全景声全面应用' },
                { icon: 'ri-global-line', title: '开放共赢', desc: '与行业伙伴深度合作，共建健康繁荣的内容生态系统' },
              ].map((val) => (
                <div key={val.title} className="bg-background-100 rounded-xl p-5 md:p-6 text-center">
                  <span className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-primary-100 text-primary-500 rounded-xl">
                    <i className={`${val.icon} text-xl`} />
                  </span>
                  <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-2">{val.title}</h4>
                  <p className="text-xs text-foreground-500 leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">用户口碑</h2>
            <p className="text-sm text-foreground-500 text-center mb-10 md:mb-14">来自真实用户的评价</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  name: '张先生',
                  role: '年卡会员·使用2年',
                  comment: '红桃视频的画质真的没得说，4K HDR看着特别舒服。内容更新也很快，热播剧基本同步上线，已经是家里电视的标配了。',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '季卡会员·使用8个月',
                  comment: '从其他平台转过来的，红桃的界面简洁好用，没有乱七八糟的广告。动漫资源很丰富，孩子特别喜欢。客服响应也很快。',
                  rating: 5,
                },
                {
                  name: '王同学',
                  role: '月卡会员·使用3个月',
                  comment: '学生党友好，月卡价格合理。推荐算法挺准的，经常能发现一些冷门好片。希望原创内容再多一些就更好了！',
                  rating: 4,
                },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-sm ${i < review.rating ? 'text-accent-500 ri-star-fill' : 'text-background-300 ri-star-line'}`} />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.comment}</p>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-semibold">
                      {review.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-xs font-medium text-foreground-800">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-10 md:mb-14">如有任何问题或合作意向，欢迎与我们取得联系</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-10">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@hongtaovideo.com', desc: '商务合作与媒体咨询' },
                { icon: 'ri-phone-line', title: '客服热线', value: '400-888-6688', desc: '7×24小时在线服务' },
                { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市朝阳区望京科技园', desc: '红桃视频总部' },
              ].map((contact) => (
                <div key={contact.title} className="bg-background-100 rounded-xl p-5 md:p-6 text-center">
                  <span className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-primary-100 text-primary-500 rounded-lg">
                    <i className={`${contact.icon} text-lg`} />
                  </span>
                  <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">{contact.title}</h4>
                  <p className="text-sm font-medium text-foreground-700 mb-1">{contact.value}</p>
                  <p className="text-xs text-foreground-400">{contact.desc}</p>
                </div>
              ))}
            </div>

            <form className="bg-background-100 rounded-xl p-5 md:p-8 max-w-2xl mx-auto" data-readdy-form="" onSubmit={handleSubmit}>
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-5 text-center">发送消息</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-600 mb-1.5">主题</label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1.5">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  required
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                />
                <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-alt-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 bg-primary-500 text-background-50 font-semibold rounded-full text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {formStatus === 'loading' ? '发送中...' : '发送消息'}
              </button>
              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center mt-3">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center mt-3">{formMessage}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}